import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { authorApi } from '../services/api';
import { Author } from '../types';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

export const AutoresPage: React.FC = () => {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authorApi
      .getAuthors()
      .then(setAuthors)
      .catch(() => setAuthors([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Autores e Equipe | {SITE_NAME}</title>
        <meta
          name="description"
          content={`Conheça quem produz e revisa o conteúdo do ${SITE_NAME}: equipe editorial, áreas de interesse e artigos publicados.`}
        />
        <link rel="canonical" href={`${SITE_URL}/autores`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <span className="text-gray-700">Autores</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Autores e Equipe</h1>
          <p className="text-gray-500 mb-8">
            Quem produz, traduz e revisa o conteúdo do {SITE_NAME}. Saiba como trabalhamos na nossa{' '}
            <Link to="/politica-editorial" className="text-blue-600 hover:underline">Política Editorial</Link>.
          </p>

          {loading ? (
            <div className="text-gray-500">Carregando autores...</div>
          ) : authors.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-8 text-gray-600">
              Os perfis da equipe editorial serão publicados em breve.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {authors.map((author) => (
                <Link
                  key={author.id}
                  to={`/autores/${author.slug}`}
                  className="bg-white rounded-xl shadow p-6 flex items-start gap-4 hover:shadow-lg transition"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xl font-bold overflow-hidden">
                    {author.photoUrl ? (
                      <img src={author.photoUrl} alt={author.name} className="w-full h-full object-cover" />
                    ) : (
                      author.name.charAt(0)
                    )}
                  </div>
                  <div>
                    <h2 className="font-bold text-gray-900">{author.name}</h2>
                    {author.role && <p className="text-blue-600 text-xs font-semibold mb-1">{author.role}</p>}
                    {author.bio && <p className="text-gray-600 text-sm line-clamp-2">{author.bio}</p>}
                    {author._count && (
                      <p className="text-gray-400 text-xs mt-2">{author._count.articles} artigos</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
